"use client";

import React, { useEffect, useState } from 'react';
import { cn } from "@/lib/utils";

interface TOCItem {
    id: string;
    text: string;
    level: number;
}

interface TableOfContentsProps {
    toc: TOCItem[];
    activeId?: string;
    locale: string;
}

export function TableOfContents({ locale }: Omit<TableOfContentsProps, 'toc' | 'activeId'>) {
    const [toc, setToc] = useState<TOCItem[]>([]);
    const [activeId, setActiveId] = useState<string>("");
    const isRtl = locale === 'ar';

    useEffect(() => {
        // Wait for hydration
        const timer = setTimeout(() => {
            const headings = Array.from(document.querySelectorAll('article h2, article h3'));
            const items = headings.map((heading) => {
                if (!heading.id) {
                    heading.id = (heading.textContent || '').toLowerCase().replace(/\s+/g, '-').replace(/[^\w\u0600-\u06FF-]/g, '') || '';
                }
                return {
                    id: heading.id,
                    text: (heading.textContent?.replace(/#$/, '').trim() || '') as string,
                    level: parseInt(heading.tagName.substring(1)),
                };
            });
            setToc(items);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (toc.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id);
                    }
                });
            },
            { rootMargin: "0px 0px -80% 0px" }
        );

        toc.forEach((item) => {
            const element = document.getElementById(item.id);
            if (element) {
                observer.observe(element);
            }
        });

        return () => {
            toc.forEach((item) => {
                const element = document.getElementById(item.id);
                if (element) {
                    observer.unobserve(element);
                }
            });
        };
    }, [toc]);

    if (!toc || toc.length === 0) return null;

    return (
        <div className="space-y-2">
            <h4 className="font-medium text-sm">{isRtl ? 'في هذه الصفحة' : 'On this page'}</h4>
            <ul className="text-sm space-y-1">
                {toc.map((item) => (
                    <li key={item.id} className={cn("relative", isRtl ? "pr-0" : "pl-0")}>
                        <a
                            href={`#${item.id}`}
                            className={cn(
                                "block py-1 transition-colors hover:text-foreground",
                                activeId === item.id
                                    ? "text-primary font-medium"
                                    : "text-muted-foreground"
                            )}
                            style={{
                                paddingLeft: !isRtl ? (item.level - 2) * 12 : undefined,
                                paddingRight: isRtl ? (item.level - 2) * 12 : undefined
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById(item.id)?.scrollIntoView({
                                    behavior: "smooth"
                                });
                                setActiveId(item.id);
                            }}
                        >
                            {item.text}
                        </a>
                        {activeId === item.id && (
                            <span
                                className={cn(
                                    "absolute top-1 bottom-1 w-0.5 bg-primary rounded-full transition-all",
                                    isRtl ? "-right-3" : "-left-3"
                                )}
                            />
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
